import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <div className="bg-[#001f3f] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-slate-300 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase heading-font">
            Privacy Policy
          </h1>
          <p className="text-slate-300 mt-2">Last updated: March 23, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white p-8 md:p-12 shadow-lg rounded-lg prose max-w-none">
          
          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">1. Introduction</h2>
          <p className="text-slate-700 mb-6">
            Welcome to SheLovesThee ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website shelovesthee.com (the "Site") and use our services.
          </p>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">2. Information We Collect</h2>
          <p className="text-slate-700 mb-4">We collect information that you provide directly to us, including:</p>
          <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
            <li><strong>Account Information:</strong> When you create an account or log in using social media (Google, Facebook, Twitter/X), we collect your name, email address, and profile picture.</li>
            <li><strong>User Content:</strong> Comments, messages, and other content you post on our platform.</li>
            <li><strong>Communications:</strong> Information you provide when you contact us.</li>
          </ul>

          <h3 className="text-xl font-bold text-[#001f3f] mb-3">Information Collected Automatically</h3>
          <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
            <li>Device information (browser type, operating system)</li>
            <li>IP address and location data</li>
            <li>Pages visited and time spent on our Site</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">3. How We Use Your Information</h2>
          <p className="text-slate-700 mb-4">We use the information we collect to:</p>
          <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
            <li>Provide, maintain, and improve our services</li>
            <li>Create and manage your account</li>
            <li>Enable you to participate in discussions and post comments</li>
            <li>Send you updates, newsletters, and promotional content (with your consent)</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Monitor and analyze usage trends</li>
            <li>Detect and prevent fraud or abuse</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">4. Social Media Login</h2>
          <p className="text-slate-700 mb-6">
            When you choose to log in using a social media account (Google, Facebook, Twitter/X, Instagram), we receive certain profile information from that social media provider. We only request the minimum information necessary to create your account (typically your name, email, and profile picture). We do not post on your behalf or access your social media contacts without your explicit permission.
          </p>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">5. Information Sharing</h2>
          <p className="text-slate-700 mb-4">We may share your information in the following circumstances:</p>
          <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
            <li><strong>Public Content:</strong> Comments and messages you post are visible to other users.</li>
            <li><strong>Service Providers:</strong> We may share information with third-party vendors who assist us in operating our Site.</li>
            <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">6. Cookies and Tracking</h2>
          <p className="text-slate-700 mb-6">
            We use cookies and similar tracking technologies to collect information about your browsing activities. You can control cookies through your browser settings. We also use Google AdSense for advertising, which may use cookies to serve personalized ads based on your interests.
          </p>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">7. Data Security</h2>
          <p className="text-slate-700 mb-6">
            We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
          </p>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">8. Your Rights</h2>
          <p className="text-slate-700 mb-4">You have the right to:</p>
          <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your account and data</li>
            <li>Opt-out of marketing communications</li>
            <li>Withdraw consent for data processing</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">9. Children's Privacy</h2>
          <p className="text-slate-700 mb-6">
            Our Site is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
          </p>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">10. Changes to This Policy</h2>
          <p className="text-slate-700 mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>

          <h2 className="text-2xl font-bold text-[#001f3f] mb-4">11. Contact Us</h2>
          <p className="text-slate-700 mb-4">
            If you have questions about this Privacy Policy, please contact us at:
          </p>
          <div className="bg-slate-100 p-4 rounded-lg">
            <p className="text-slate-700"><strong>SheLovesThee Podcast</strong></p>
            <p className="text-slate-700">Email: contact@shelovesthee.com</p>
            <p className="text-slate-700">Website: https://shelovesthee.com</p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
